/**
 * Content script for https://gemini.google.com/*
 *
 * Google Gemini does not update document.title with the active conversation name,
 * so Chrome's tab title stays as the generic "Gemini".  This script reads the
 * conversation title directly from the sidebar DOM and sends it to the Tabvana
 * background service worker so a more descriptive title can be stored.
 *
 * Inspired by https://www.benjaminoakes.com/2025/12/03/Userscript-Google-Gemini-Tab-Renamer/
 *
 * Security audit (2026-04-01): No innerHTML or eval usage found. Title extraction
 * uses textContent only (safe, text-only, no HTML parsing). Sanitisation of
 * incoming titles is handled downstream in dbSync.ts (sanitizeTitle).
 */

const DEBOUNCE_MS = 500;

let lastSentTitle = '';
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

function getConversationTitle(): string | null {
  // Prefer the element that is currently selected/active in the conversation list.
  // Fall back to the first visible .conversation-title if none is marked selected.
  const el =
    document.querySelector<HTMLElement>('[data-selected="true"] .conversation-title') ??
    document.querySelector<HTMLElement>('.conversation-title');
  return el?.textContent?.trim() || null;
}

function sendTitleOverride(title: string): void {
  chrome.runtime.sendMessage({ type: 'titleOverride', url: location.href, title }).catch(() => {
    // Swallow — service worker may be momentarily inactive; the next DOM change
    // will trigger a retry via the MutationObserver.
  });
}

function checkAndMaybeSend(): void {
  const title = getConversationTitle();
  if (title && title !== lastSentTitle) {
    lastSentTitle = title;
    sendTitleOverride(title);
  }
}

function scheduleCheck(): void {
  if (debounceTimer !== null) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(checkAndMaybeSend, DEBOUNCE_MS);
}

// Initial check once the page is idle
checkAndMaybeSend();

// Watch for DOM changes — Gemini is a SPA; conversation titles load and change dynamically
new MutationObserver(scheduleCheck).observe(document.documentElement, {
  subtree: true,
  childList: true,
  characterData: true,
});
